import { createContext } from 'react';

/**
 * @typedef {object} ModalContextState
 * @property {string[]} skus Item SKUs for the order's shipper, in the order the query returns them. Feeds the
 * item SKU picker.
 * @property {object} order The draft order (the clone), not the `order` prop. Every tab reads and edits this.
 * @property {object} serviceCatalog The shipper's service definitions, used by the Services tab.
 * @property {Error} [serviceCatalogError] Set when the catalog failed to load.
 * @property {boolean} serviceCatalogLoading True while the catalog is loading.
 * @property {string} serviceCatalogShipperId The shipper whose catalog was loaded: the order's shipper, else the
 * current user's. A quick add writes to and refreshes this shipper.
 * @property {string[]} tabs The `EDIT_ORDER_TABS` shown for this order and user, in render order.
 * @property {typeof DEFAULT_OPTIONS & object} options The caller's `opt` merged over `DEFAULT_OPTIONS`.
 * @property {string} [initTab] A tab to scroll to when the modal opens.
 * @property {number} [initTabOffset] Pixels to leave above `initTab` when scrolling to it.
 * @property {Record<string, boolean>} hasError Field name to "this field is invalid". A field may be invalid
 * before the user touches it; use `isDirty` to decide whether to show it.
 * @property {Record<string, boolean>} hasWarning Field name to "valid but worth a second look", such as a
 * phone number that looks wrong.
 * @property {boolean} adminHasError True when an admin-only rule fails, such as a rate override changed without
 * an Invoicing note.
 * @property {Record<string, boolean>} hasErrorTab Tab id to "this tab has an error". Drives the red tab marker.
 * @property {{ geocodePartialMatch?: boolean, geocodeFailed?: boolean }} errors Results of the last address
 * lookup. It is sent to `onSave` as `errors` so the caller can flag an address that did not fully match.
 * @property {Record<string, boolean>} isDirty Field name to "the user edited this field". Errors on an untouched
 * field show only when `options.startDirty` is true.
 * @property {object|null} createSingleOrderManifest The inbound warehouse manifest (warehouse, receiving date,
 * the order's items) to create after the save, or `null` for none. Set from the Warehouse tab.
 * @property {(manifest: object|null) => void} setCreateSingleOrderManifest Sets or clears the manifest request.
 * @property {boolean} sendCarrierAssignedEmail True when the carrier should get an assignment email after the save.
 * @property {(send: boolean) => void} setSendCarrierAssignedEmail Turns that email on or off.
 * @property {object|null} savedCustomer The saved customer last picked into the order, or `null`.
 * @property {(checked: boolean) => void} setSaveCustomer Sets the "Save Customer" box. When checked, the save
 * also stores the order's customer details as a saved customer.
 * @property {{ private: boolean, notify: boolean, note: string, type: string|null }} noteInfo The new note being
 * written on the Notes tab. It is added to the order on save.
 */

/**
 * @typedef {object} ModalContextCallbacks
 * @property {(error: Error) => void} onError Reports a failed or blocked step to the caller.
 * @property {(payload: object) => (void|Promise<void>)} onSave The caller's save handler. It writes the order.
 * @property {() => void} onClose Closes the modal.
 * @property {(fields: object) => void} modifyOrder Merges `fields` into the draft. Comes from
 * `useEditModalCallbacks`, as do the other field, address, and geocoding callbacks the tabs call (`makeDirty`
 * and so on). The caller's own callbacks are also spread in here.
 * @property {(customer: object) => void} onSavedCustomerSelect Fills the order's address and contact fields from
 * a saved customer.
 * @property {(options: object) => Promise<unknown>} deleteSavedCustomer Deletes a saved customer. Failures go to
 * Sentry.
 * @property {(note: object) => void} setNoteInfo Replaces the new-note draft.
 * @property {(leg?: string) => Promise<void>} refetchOrder Call after the Admin tab unassigns a carrier. It runs
 * the caller's `refetchOrder`, reads the stored carrier assignment for `leg`, and merges just those fields into
 * the draft, so other unsaved edits survive. It throws if the read fails.
 * @property {(write: () => Promise<unknown>) => Promise<unknown>} runOwnWrite Wraps a server write the user
 * started in the modal. The live feed ignores version changes until `write` settles, so the user is not
 * told about their own change.
 * @property {() => void} refreshServiceCatalogs Reloads the service catalogs.
 */

/**
 * Shared state and callbacks for the Edit Order tabs, built by `useModalContextValue`. Read it with
 * `useContext(ModalContext)`. `EditOrderModal/index.jsx` re-exports it.
 *
 * @type {import('react').Context<{ state: ModalContextState, callbacks: ModalContextCallbacks }>}
 */
export const ModalContext = createContext();

/**
 * No-op handlers the modal falls back to when the caller's `callbacks` prop leaves one out, so the modal can
 * call them without checking.
 */
export const DEFAULT_CALLBACKS = {
    onError: () => {},
    onSave: () => {},
    onClose: () => {},
};

/** Defaults for the modal's `opt` prop. The caller's `opt` is merged over these. */
export const DEFAULT_OPTIONS = {
    /** Locks the address fields on the Origin and Pickup tabs. */
    disableAddressEditing: false,
    /** Shows the pickup date fields on the Pickup tab in place of the "First Available" date picker. */
    enablePickupDateEditing: false,
    /** Includes test accounts in the Admin tab's shipper and carrier lists. */
    enableAdminTestAccounts: false,
    /**
     * Skips address lookups when the user picks an address, and does not require a successful geocode. For import
     * review, where addresses are checked elsewhere.
     */
    disableGeocoding: false,
    /** Shows validation errors before the user touches a field. When false, an error shows only after an edit. */
    startDirty: true,
    /** On the Customer tab, warns when a delivery date is set but the customer's availability for it is unknown. */
    customerAvailabilityToggle: false,
    /**
     * The order is view-only. Done is disabled and the live feed does not subscribe. Ignored for Onward and
     * corporate admins, who can edit invoiced orders.
     */
    readOnly: false,
};
